import React, { useEffect, useRef, useState, useCallback } from "react";
import BrowseSection from "../BrowseSection/BrowseSection";
import "./StickyBrowseSection.styles.scss";

export interface StickyBrowseSectionProps extends React.ComponentProps<typeof BrowseSection> {}

const StickyBrowseSection: React.FC<StickyBrowseSectionProps> = (props) => {
    const [isStickyVisible, setIsStickyVisible] = useState(false);
    const [stickyTop, setStickyTop] = useState<number>(0);
    const [stickyLeft, setStickyLeft] = useState<number>(0);
    const [stickyWidth, setStickyWidth] = useState<number | string>("100%");
    const sensorRef = useRef<HTMLDivElement>(null);
    const fixedRef = useRef<HTMLDivElement>(null);
    const [browseHeight, setBrowseHeight] = useState<number>(0);

    const updateHeaderHeight = useCallback(() => {
        const header = document.querySelector<HTMLElement>(".PlatformHeader");
        if (header) {
            const rect = header.getBoundingClientRect();
            setStickyTop(rect.bottom);
        }
    }, []);

    const updateDimensions = useCallback(() => {
        const target = isStickyVisible ? fixedRef.current : sensorRef.current;
        if (!target) return;

        const rect = target.getBoundingClientRect();
        setStickyLeft(rect.left);
        setStickyWidth(rect.width);
        setBrowseHeight(rect.height);

        window.dispatchEvent(new Event("sticky-browse-dimensions"));
    }, [isStickyVisible]);

    useEffect(() => {
        updateHeaderHeight();
        updateDimensions();

        window.addEventListener("resize", updateHeaderHeight);
        window.addEventListener("resize", updateDimensions);

        return () => {
            window.removeEventListener("resize", updateHeaderHeight);
            window.removeEventListener("resize", updateDimensions);
        };
    }, [updateHeaderHeight, updateDimensions]);

    useEffect(() => {
        updateDimensions();
    }, [isStickyVisible, updateDimensions]);

    useEffect(() => {
        if (!sensorRef.current) return;

        const observerCallback: IntersectionObserverCallback = ([entry]) => {
            setIsStickyVisible(!entry.isIntersecting);
        };

        const observer = new IntersectionObserver(observerCallback, {
            root: null,
            rootMargin: `-${stickyTop}px 0px 0px 0px`,
            threshold: 0
        });

        observer.observe(sensorRef.current);

        return () => observer.disconnect();
    }, [stickyTop]);

    useEffect(() => {
        const offset = stickyTop + browseHeight;
        document.documentElement.style.setProperty("--table-header-offset", `${offset}px`);

        return () => {
            document.documentElement.style.removeProperty("--table-header-offset");
        };
    }, [stickyTop, browseHeight]);

    // Re-measure whenever the currently visible header (original or sticky) changes size
    useEffect(() => {
        const target = isStickyVisible ? fixedRef.current : sensorRef.current;
        if (!target) return;

        const resizeObserver = new ResizeObserver(() => updateDimensions());
        resizeObserver.observe(target);

        return () => resizeObserver.disconnect();
    }, [isStickyVisible, updateDimensions]);

    const prevStickyVisible = useRef(isStickyVisible);
    useEffect(() => {
        if (isStickyVisible && !prevStickyVisible.current) {
            document.dispatchEvent(new MouseEvent("click", { bubbles: true }));
        }
        prevStickyVisible.current = isStickyVisible;
    }, [isStickyVisible]);

    useEffect(() => {
        const activeElem = document.activeElement as HTMLElement | null;
        const isSearchFocused = activeElem?.classList.contains("BrowseSection__search-input");

        if (!isSearchFocused) return;

        const targetContainer = isStickyVisible ? fixedRef.current : sensorRef.current;
        const targetInput = targetContainer?.querySelector<HTMLInputElement>(".BrowseSection__search-input");

        if (targetInput) {
            setTimeout(() => targetInput.focus(), 0);
        }
    }, [isStickyVisible]);

    return (
        <>
            <div ref={sensorRef} className={`StickyBrowseSection__original ${isStickyVisible ? "is-hidden" : ""}`}>
                <BrowseSection {...props} />
            </div>
            <div
                ref={fixedRef}
                className={`StickyBrowseSection__fixed ${isStickyVisible ? "is-visible" : ""}`}
                style={{
                    top: stickyTop,
                    left: props.viewMode === "tile" ? "-1px" : stickyLeft,
                    width: props.viewMode === "tile" ? "100%" : stickyWidth,
                    height: "auto",
                    padding: props.viewMode === "tile" ? "0 50px" : "0",
                    visibility: isStickyVisible ? "visible" : "hidden"
                }}
            >
                <BrowseSection {...props} />
            </div>
        </>
    );
};

export default StickyBrowseSection;
